import React, {
  useCallback,
  useContext,
  useEffect, useMemo,
  useRef,
} from 'react';
import style from './style';
import Header from './Header';
import { Context } from '../../../states/Store';
import {
  getContextInstanceThirdParty,
} from '../../../services/edge/ContexteService';
import i18next from 'i18next';
import Attributs from './Attributs';
import { FormProvider, useForm } from 'react-hook-form';
import EAAccordion from '../../../components/EAAccordion';
import Visualisation from './Visualisation';
import Notes from './Notes';
import AssociatedTiers from './AssociatedTiers';
import { Grid } from '@mui/material';
import EAButton from '../../../components/common/EAButton';
import EAIcon from '../../../components/common/EAIcon';
import ROUTES from '../../../config/Routes';
import { useHistory } from 'react-router-dom';
import FicheContextContent from '../../../components/context/FicheContext/Content';
import FicheContextMenu from '../../../components/context/FicheContext/Menu';
import Informations from './Informations';
import { useQuery } from '../../../config/Config';
import { useLocation } from 'react-router';
import ModificationDate from '../../../components/context/ModificationDate';
import moment from 'moment';
import CreatedBy from '../../../components/context/CreatedBy';
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from '../../../services/common/RolesUtils';

const navigationItems = [
  {
    id: 'attributs',
    icon: 'context/navigation/attributs',
    label: 'context.contextDetail.navigation.attributs',
  },
  {
    id: 'visualisation',
    icon: 'context/navigation/visualisation',
    label: 'context.contextDetail.navigation.visualisation',
  },
  {
    id: 'tiers',
    icon: 'context/navigation/tiers',
    label: 'context.contextDetail.navigation.tiers',
  },
  {
    id: 'notes',
    icon: 'context/navigation/notes',
    label: 'context.contextDetail.navigation.notes',
  },
];

export const getLatestUpdateDateFormatted = (attrs, prop = 'updateDate') => {
  if (!attrs) return null;
  const validDates = attrs
    .map((a) => a[prop])
    .filter((date) => moment(date).isValid());

  if (validDates.length === 0) return null;

  const latest = validDates.reduce((max, curr) =>
    moment(curr).isAfter(max) ? curr : max
  );

  return moment(latest).format('DD/MM/YYYY');
};

const ContextDetail = ({ contextInstanceList, fetchContextInstance, onClose, selectedContext, setSelectedInstanceId}) => {
  const { classes } = style();
  const queryUtil = useQuery();
  const location = useLocation();
  const navigationRef = useRef();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const associatedTP = state.contextThirdParties;
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);



  const formMethods = useForm({
    mode: 'onChange',
    delayError: 500,
    defaultValues: {
      id: null,
      expirationDate: '',
      contextModelName: '',
      description: '',
      reference: '',
      contextAttributes: [],
      tiersAttributes: [],
      demandeAttributes: [],
    },
  });

  const { reset, getValues } = formMethods;

  const contextAttributes = getValues('contextAttributes');

  const filteredNavigationItems = useMemo(() => {
    return navigationItems.filter((item) => {
      if (item.id === 'attributs') return contextAttributes.length > 0;
      if (item.id === 'visualisation') return associatedTP.length > 0;
      return true;
    });
  }, [contextAttributes.length, associatedTP.length]);

  useEffect(() => {
    return () => {
      dispatch({ type: 'SELECTED_CONTEXT_INSTANCE', contextInstance: null });
      dispatch({ type: 'SET_CONTEXT_THIRDPARTIES', payload: []})
    };
  }, [dispatch]);


  const fetchAssociatedTP = useCallback(async () => {
    if (selectedContext) {
      await getContextInstanceThirdParty(
        state.account.id,
        selectedContext.id,
        dispatch
      );
    }
  }, [selectedContext, state.account.id, dispatch]);

  useEffect(() => {

    if (selectedContext) {
      fetchAssociatedTP();
      reset(selectedContext);
    }
  }, [
    selectedContext,
    fetchAssociatedTP,
    history,
    location.pathname,
    queryUtil,
    reset,
  ]);

  const onEditAttribute = () => {
    fetchContextInstance();
  };

  const contextAttributesUpdateDate = getLatestUpdateDateFormatted(
    getValues('contextAttributes')
  );

  return (
    <>
      <Header
        context={selectedContext}
        contextList={contextInstanceList}
        setSelectedInstanceId={setSelectedInstanceId}
        closeDialog={onClose}
        fetchContextInstance={fetchContextInstance}
      />

      <FicheContextContent ref={navigationRef}>
        {selectedContext && (
          <CreatedBy
            author={selectedContext.createdBy}
            creationDate={selectedContext.creationDate}
          />
        )}
        <Informations context={selectedContext} fetchContextInstance={fetchContextInstance}/>
        <FicheContextMenu
          navigationItems={filteredNavigationItems}
          ref={navigationRef}
          additionalClass={classes.menu}
        />
        <FormProvider {...formMethods}>
          {selectedContext ? (
            <>
              <Grid container spacing={2}>
                {hasAnyOfPrivileges(
                  PrivilegeEnum.PRIVILEGE_VIEW_CONTEXT_ATTRIBUTES
                ) &&
                  getValues('contextAttributes').length ? (
                    <Grid item xs={12} id={'attributs'}>
                      <EAAccordion
                        title={i18next.t(
                          `context.contextDetail.blocks.attributs`
                        )}
                        logo="/context/attributs_block"
                        titleNoResult={true}
                        opened={true}
                        buttons={
                          contextAttributesUpdateDate
                            ? [
                                <ModificationDate
                                  date={contextAttributesUpdateDate}
                                />,
                              ]
                            : []
                        }
                      >
                        <Attributs
                          onEditAttribute={onEditAttribute}
                          isContextArchived={selectedContext.isArchived}
                        />
                      </EAAccordion>
                    </Grid>
                  ):<></>}
                {associatedTP.length ? (
                  <Grid item xs={12} id={'visualisation'}>
                    <EAAccordion
                      logo={'/context/visualisation_block'}
                      title={i18next.t(
                        `context.contextDetail.blocks.visualisation`
                      )}
                      titleNoResult={true}
                      opened={true}
                    >
                      <Visualisation
                        context={selectedContext}
                        associatedTP={associatedTP}
                        fetchContextInstance={fetchContextInstance}
                        fetchAssociatedTP={fetchAssociatedTP}
                      />
                    </EAAccordion>
                  </Grid>
                ) : (
                  <></>
                )}
                <Grid item xs={12} id={'tiers'}>
                  <EAAccordion
                    logo={'/drawer/thirdparty_list'}
                    title={
                      <>
                        <span>
                          {i18next.t(`context.contextDetail.blocks.tiers`)}
                        </span>
                        <EAButton
                          onClick={() => {
                            history.push(
                              `${ROUTES.CONTEXT_DETAIL_ADD_TP}?instance=${selectedContext.id}`
                            );
                          }}
                          content={
                            <>
                              <EAIcon icon={'context/addAssocier'} />
                              <span style={{ marginLeft: '5px' }}>
                                {i18next.t('context.addTiers.add')}
                              </span>
                            </>
                          }
                          customStyle={{
                            position: 'absolute',
                            right: 50,
                            marginLeft: 'auto',
                          }}
                          disabled={
                            selectedContext.isArchived ||
                            !hasAnyOfPrivileges(
                              PrivilegeEnum.PRIVILEGE_ADD_THIRDPARTY_CONTEXT
                            )
                          }
                        />
                      </>
                    }
                    titleNoResult={true}
                    opened={true}
                  >
                    <AssociatedTiers
                      context={selectedContext}
                      associatedTP={associatedTP}
                    />
                  </EAAccordion>
                </Grid>
                <Grid item xs={12} id={'notes'}>
                  <Notes contextInstance={selectedContext} />
                </Grid>
              </Grid>
            </>
          ) : (
            ''
          )}
        </FormProvider>
      </FicheContextContent>
    </>
  );
};

export default ContextDetail;
